import { normalize } from 'normalizr';
import { push } from 'react-router-redux';
import moment from 'moment';
import { showLoading, hideLoading } from 'react-redux-loading-bar';
import download from 'downloadjs';
import { snakeCase, toLower } from 'lodash';

import {
  getById,
  getNextAvailableTimeKey,
} from '../selector';
import * as schema from '../schema';
import * as types from '../constants';
import Http from '../../../http';
import { extractAppointments } from '../../../common/helpers/appointments';
import { prepareDayForSaving } from '../../../common/helpers/schedules';
import { fullName } from '../../../common/helpers/users';
import config from '../../../../config';
import { ChurnZeroService, ADD_BARBER, DELETE_BARBER } from '../../../../modules/infra/observability/churnzero';
import { RudderstackTracker, BLOCK_TIME_CREATED } from '../../../../modules/infra/observability/rudderstack';

export const include = [
  'shop',
  'schedules',
  'services',
  'photos',
  'days_on',
  'commissionTiers',
  'productCommissionTiers',
];

export const fetchShopBarbers = (shopId, searchText = '', additionalInclude = []) => {
  let apiFilter = {
    include: [...include, ...additionalInclude].join(','),
    limit: 200,
    sortBy: 'order:asc,createdAt:desc',
  };

  if (searchText) {
    apiFilter = {
      include: apiFilter.include,
      limit: apiFilter.limit,
      or: true,
      firstName__like: searchText,
      lastName__like: searchText,
      phone__like: searchText,
      email__like: searchText,
      sortBy: 'order:asc,createdAt:desc',
    };
  }

  return Http.get(`/shop/${shopId}/barber`, apiFilter, true, {
    'Squire-Version': '2019-12-25',
  });
};

export function getBarbers(shopId, searchText = '', additionalInclude = []) {
  return (dispatch) => {
    dispatch(showLoading());
    return fetchShopBarbers(shopId, searchText, additionalInclude)
      .then((barbers) => {
        dispatch({
          type: types.GET_BARBERS,
          response: normalize(barbers, schema.arrayOfBarbers),
        });
        dispatch(hideLoading());

        return barbers;
      })
      .catch((err) => {
        dispatch(hideLoading());
        return err;
      });
  };
}

const updateBarberOrderAction = barbers => ({
  type: types.UPDATE_BARBER_ORDER,
  barbers,
});

export function updateBarberOrder(shopId, barbers = []) {
  return (dispatch) => {
    dispatch(updateBarberOrderAction(barbers));
    return Http.put(`/shop/${shopId}/barber/order`, barbers)
      .then(() => dispatch(getBarbers(shopId)));
  };
}

export function getBarber(barberId) {
  return dispatch => Http.get(`/barber/${barberId}`, {
    include: [...include, 'days_off'].join(','),
  },
    true,
    { 'Squire-Version': '2022-09-02' }).then((barber) => {
      dispatch({
        type: types.IMPORT_SINGLE_BARBER,
        response: normalize(barber, schema.barber),
      });
      return barber;
    });
}

export function barberUpdated(barberId) {
  return dispatch => Http.get(`/barber/${barberId}`, {
    include: include.join(','),
  }).then((barber) => {
    dispatch({
      type: types.BARBER_UPDATED,
      response: normalize(barber, schema.barber),
    });
    return barber;
  });
}

export function updateBarber(barberId, data) {
  return dispatch =>
    Http.put(`/barber/${barberId}`, data).then(() =>
      Http.get(`/barber/${barberId}`, {
        include: include.join(','),
      }).then((barber) => {
        dispatch({
          type: types.UPDATE_BARBER,
          response: normalize(barber, schema.barber),
        });
        return barber;
      })
    );
}

export function addBarber(data) {
  return dispatch => Http.post('/barber', data).then((barber) => {
    dispatch({
      type: types.ADD_BARBER,
      response: normalize(barber, schema.barber),
    });
    dispatch(getBarbers(data.shopId));
    ChurnZeroService.track(ADD_BARBER);
    return barber;
  });
}

export function addBarberImage(barberId, image) {
  return dispatch => Http.postRaw(`/barber/${barberId}/photo`, {
    image,
    enabled: true,
    order: 0,
  }).then((photo) => {
    dispatch({ type: types.ADD_BARBER_PHOTO });
    return photo;
  });
}

export function changeBarberImage(barberId, oldImage, newImage) {
  return (dispatch, getState) => Http.delete(`/barber/${barberId}/photo/${oldImage.id}`)
    .then(() => addBarberImage(barberId, newImage)(dispatch, getState));
}

export const fetchBarberAppointments = (shopId, barberId, filter = {}) => {
  const apiFilter = {
    barberId,
    shopId,
    include: 'barber,customer,shop,appointment_service,payment,promos.customer_promo.promo,bookedBy',
    sortBy: filter.sortBy || 'dateTime:asc',
    limit: filter.limit || 30,
  };

  if (filter.start) {
    apiFilter.dateTime__gte = filter.start;
  }

  if (filter.end) {
    apiFilter.dateTime__lte = filter.end;
  }

  return Http.get('/appointment', apiFilter).then(extractAppointments);
};

export const getBarberAppointmentsForProfile = (shopId, barberId, timezone) => async (dispatch) => {
  const now = moment.tz(timezone).toISOString();
  const futureApptsPromise = fetchBarberAppointments(shopId, barberId, { start: now });
  const pastApptsPromise = fetchBarberAppointments(shopId, barberId, {
    end: now,
    limit: 10,
    sortBy: 'dateTime:desc',
  });
  const [futureAppts, pastAppts] = await Promise.all([futureApptsPromise, pastApptsPromise]);

  const appointments = [...futureAppts, ...pastAppts];
  dispatch({ type: types.IMPORT_SINGLE_BARBER_APPOINTMENTS, appointments });
  return appointments;
};

export const resetAvailableTimes = () => ({ type: types.RESET_AVAILABLE_TIMES });

const requestAvailableTimes = params => ({ type: types.REQUEST_AVAILABLE_TIMES, payload: params });
const receiveAvailableTimes = days => ({
  type: types.RECEIVE_AVAILABLE_TIMES,
  payload: days,
});

export const fetchAvailableTimes = (() => {
  let actionId = null;
  return (barberId, start, end, duration, appointmentId) => (dispatch) => {
    actionId = Date.now();
    const currentActionId = actionId;
    dispatch(requestAvailableTimes({ duration }));

    if (!barberId) {
      const emptyDays = [];
      const endMoment = moment(end);
      const current = moment(start);
      while (current.isSameOrBefore(endMoment, 'd')) {
        emptyDays.push({ day: current.format(config.dateFormatISO) });
        current.add(1, 'd');
      }
      return dispatch(receiveAvailableTimes(emptyDays));
    }

    let endpoint = `/barber/${barberId}/available-time-range/${start}/${end}/${duration}`;
    if (appointmentId) {
      endpoint += `?excludeAppointmentId=${appointmentId}`;
    }
    return Http.get(endpoint).then((days) => {
      if (actionId === currentActionId) return dispatch(receiveAvailableTimes(days));
      return null;
    });
  };
})();

export function deleteBarber(shopId, barberId) {
  return dispatch => Http.delete(`/barber/${barberId}`).then((res) => {
    dispatch({ type: types.DELETE_BARBER, barberId });
    dispatch(getBarbers(shopId));
    ChurnZeroService.track(DELETE_BARBER);
    return res;
  });
}

export function confirmBarber(shopId, barberId) {
  return dispatch => Http.post(`/shop/${shopId}/confirm/${barberId}`)
    .then(() => dispatch(getBarber(barberId)));
}

export const addBlockTimeAction = data => ({
  type: types.ADD_BLOCK_TIME,
  data,
});

export function addBlockTime(data, forceSave = false) {
  const endpoint = `/barber/${data.barberId}/days-off`;
  const toSave = prepareDayForSaving(data);
  toSave.forceSave = forceSave;
  return (dispatch) => {
    dispatch(addBlockTimeAction(toSave));
    return Http.post(endpoint, toSave).then(() => {
      RudderstackTracker.getInstance().track(BLOCK_TIME_CREATED);
      return dispatch(getBarber(data.barberId));
    });
  };
}

export const editBlockTimeAction = data => ({
  type: types.EDIT_BLOCK_TIME,
  data,
});

export function editBlockTime(data, forceSave = false) {
  const endpoint = `/barber/${data.barberId}/days-off/${data.id}`;
  const toSave = prepareDayForSaving(data);
  toSave.forceSave = forceSave;
  return (dispatch) => {
    dispatch(editBlockTimeAction(toSave));
    return Http.put(endpoint, toSave).then(() => dispatch(getBarber(data.barberId)));
  };
}

export const deleteBlockTimeAction = data => ({
  type: types.DELETE_BLOCK_TIME,
  data,
});

export function deleteBlockTime(barberId, id, all = false) {
  const endpoint = `/barber/${barberId}/days-off/${id}`;
  return (dispatch) => {
    dispatch(deleteBlockTimeAction(id));
    return Http.delete(endpoint, { deleteAllRecurring: all }).then(() => dispatch(getBarber(barberId)));
  };
}

const fetchNextAvailableTimeAction = id => ({
  type: types.FETCH_NEXT_AVAILABLE_TIMES,
  payload: id,
});

const fetchNextAvailableTimeSuccess = id => ({
  type: types.FETCH_NEXT_AVAILABLE_TIMES_SUCCESS,
  payload: {
    id,
    updatedAt: Date.now(),
  },
});

const fetchNextAvailableTimeError = id => ({
  type: types.FETCH_NEXT_AVAILABLE_TIMES_ERROR,
  payload: id,
});

export const fetchNextAvailableTime = (barberId, duration) => (dispatch) => {
  if (!barberId || !duration) return null;

  const id = getNextAvailableTimeKey(barberId, duration);

  dispatch(fetchNextAvailableTimeAction(id));

  return Http.get(`/barber/${barberId}/next-available-time/${duration}`)
    .then((response) => {
      dispatch(fetchNextAvailableTimeSuccess(id));

      return response.time || null;
    })
    .catch(() => dispatch(fetchNextAvailableTimeError(id)));
};

export const generateBarberShortLink = barberId => dispatch =>
  Http.post(`/barber/${barberId}/short-link`).then(() => dispatch(getBarber(barberId)));

export const redirectToBarberList = () => dispatch => dispatch(push('/barbers'));

export function restoreDeletedBarber(shopId, barberId) {
  return dispatch => Http.put(`/barber/${barberId}/restore`)
    .then((barber) => {
      dispatch({
        type: types.RESTORE_SINGLE_BARBER,
        payload: barber,
      });
      dispatch(getBarbers(shopId));
    });
}

export const generateShopBarberPermissionPasscode = (brandId, barberId) => (dispatch, getState) => {
  const barber = getById(getState(), barberId);

  return Http.post(`/brand/${brandId}/barber/${barber.barberId}/generate-permission-passcode`).then(() =>
    dispatch(getBarber(barberId))
  );
};


export const setShopBarberPermissionPasscode = (brandId, barberId, code) => (dispatch, getState) => {
  dispatch({ type: types.BARBER_SET_PERMISSION_CODE_LOADING });

  const barber = getById(getState(), barberId);

  return Http.post(`/brand/${brandId}/barber/${barber.barberId}/set-permission-passcode`, { code })
    .finally(() => dispatch({ type: types.BARBER_SET_PERMISSION_CODE_LOADED }))
    .then(() =>
      dispatch(getBarber(barberId))
    );
};

export const addBarberDay = (barberId, dayType, data) => {
  const endpoint = `/barber/${barberId}/days-${dayType}`;
  return Http.post(endpoint, data);
};

// shops don't have a separate endpoint for days off
// just using it as a noop since barbers and shops share the same days on/off component
export const addShopDay = () => Promise.resolve();


export const removeBarberDay = (barberId, dayType, id) => {
  const endpoint = `/barber/${barberId}/days-${dayType}/${id}`;
  return Http.delete(endpoint);
};

// shops don't have a separate endpoint for days off
// just using it as a noop since barbers and shops share the same days on/off component
export const removeShopDay = () => Promise.resolve();

export const downloadScheduleHistory = barber => Http
  .getRaw(`/barber/${barber.id}/schedule-history`, {
    view: 'csv',
    limit: 30,
    sortBy: 'createdAt:desc',
  }, true)
  .then(response =>
    download(response, `${snakeCase(toLower(fullName(barber)))}.csv`, 'text/plain')
  );
